<template>
  <div class="orangehrm-background-container">
    <div class="orangehrm-card-container">
      <oxd-text class="orangehrm-main-title" tag="h6">
        {{ $t('payroll.add_deductions') }}
      </oxd-text>
      <oxd-divider />
      <oxd-form :loading="isLoading" @submit-valid="onSave">
        <oxd-form-row>
          <oxd-grid :cols="3" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <employee-autocomplete
                v-model="deduction.employee"
                :rules="rules.employee"
                required
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <oxd-input-field
                v-model="deduction.deductionName"
                :rules="rules.name"
                :label="$t('payroll.deduction_name')"
                required
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <oxd-input-field
                v-model="deduction.deductionType"
                type="select"
                :rules="rules.type"
                :options="typeOptions"
                :label="$t('payroll.deduction_type')"
                required
              />
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>
        <oxd-form-row>
          <oxd-grid :cols="3" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <oxd-input-field
                v-model="deduction.amount"
                :rules="rules.amount"
                :label="$t('payroll.deduction_amount')"
                required
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <oxd-input-field
                v-model="deduction.isPercentage"
                type="select"
                :options="percentageOptions"
                :label="$t('payroll.percentage')"
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <oxd-input-field
                v-model="deduction.currencyId"
                type="select"
                :rules="rules.currency"
                :options="currencies"
                :label="$t('payroll.currency_id')"
                required
              />
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>
        <oxd-form-row>
          <oxd-grid :cols="2" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <date-input
                v-model="deduction.effectiveFrom"
                :label="$t('payroll.effective_from')"
                :rules="rules.effectiveFrom"
                required
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <date-input
                v-model="deduction.effectiveTo"
                :label="$t('payroll.effective_to')"
                :rules="rules.effectiveTo"
                required
              />
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>
        <oxd-form-row>
          <oxd-grid :cols="1" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <oxd-input-field
                v-model="deduction.comments"
                type="textarea"
                :label="$t('general.comment')"
                :rules="rules.comments"
                required
              />
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>
        <oxd-divider />
        <oxd-form-actions>
          <required-text />
          <div class="orangehrm-button-row">
            <oxd-button
              display-type="ghost"
              :label="$t('general.cancel')"
              @click="onCancel"
            />
            <submit-button />
          </div>
        </oxd-form-actions>
      </oxd-form>
    </div>
  </div>
</template>

<script>
import {navigate} from '@/core/util/helper/navigation';
import EmployeeAutocomplete from '@/core/components/inputs/EmployeeAutocomplete';
import {APIService} from '@/core/util/services/api.service';
import {
  required,
  validSelection,
  validDateFormat,
  endDateShouldBeAfterStartDate,
  startDateShouldBeBeforeEndDate,
  digitsOnly,
} from '@/core/util/validation/rules';
import useDateFormat from '@/core/util/composable/useDateFormat';

const deductionModel = {
  employee: null,
  deductionName: '',
  deductionType: null,
  amount: '',
  currencyId: '',
  isPercentage: false,
  effectiveFrom: '',
  effectiveTo: '',
  comments: '',
};

export default {
  components: {
    'employee-autocomplete': EmployeeAutocomplete,
  },
  props: {
    currencies: {
      type: Array,
      default: () => [],
    },
  },
  setup() {
    const {userDateFormat} = useDateFormat();
    const http = new APIService(
      window.appGlobal.baseUrl,
      '/api/v2/payroll/deductions',
    );
    return {
      http,
      userDateFormat,
    };
  },
  data() {
    return {
      isLoading: false,
      deduction: {...deductionModel},
      rules: {
        comments: [required],
        amount: [required, digitsOnly],
        name: [required],
        currency: [required, validSelection],
        type: [required, validSelection],
        employee: [required, validSelection],
        effectiveFrom: [
          required,
          validDateFormat(this.userDateFormat),
          startDateShouldBeBeforeEndDate(
            () => this.deduction.effectiveTo,
            this.$t('payroll.effective_from_should_be_before_to'),
          ),
        ],
        effectiveTo: [
          required,
          validDateFormat(this.userDateFormat),
          endDateShouldBeAfterStartDate(
            () => this.deduction.effectiveFrom,
            this.$t('payroll.effective_to_should_be_after_from'),
          ),
        ],
      },
      percentageOptions: [
        {
          id: 1,
          param: 'yes',
          label: this.$t('general.yes'),
        },
        {
          id: 2,
          param: 'no',
          label: this.$t('general.no'),
        },
      ],
      typeOptions: [
        {
          id: 1,
          param: 'tax',
          label: this.$t('payroll.tax'),
        },
        {
          id: 2,
          param: 'insurance',
          label: this.$t('payroll.insurance'),
        },
        {
          id: 3,
          param: 'loan',
          label: this.$t('payroll.loan'),
        },
        {
          id: 4,
          param: 'advance',
          label: this.$t('payroll.advance'),
        },
        {
          id: 5,
          param: 'other',
          label: this.$t('payroll.other'),
        },
      ],
    };
  },
  methods: {
    onCancel() {
      navigate('/payroll/viewPayrollDeductions');
    },
    onSave() {
      this.isLoading = true;
      this.http
        .create({
          empNumber: this.deduction.employee.id,
          deductionName: this.deduction.deductionName,
          deductionType: this.deduction.deductionType?.param,
          amount: this.deduction.amount,
          currencyId: this.deduction.currencyId?.id,
          isPercentage: this.deduction.isPercentage?.param === 'yes',
          effectiveFrom: this.deduction.effectiveFrom,
          effectiveTo: this.deduction.effectiveTo,
          comments: this.deduction.comments,
          isActive: true,
        })
        .then(() => {
          return this.$toast.updateSuccess();
        })
        .then(() => {
          this.onCancel();
        })
        .catch((response) => {
          return this.$toast.warn({
            title: this.$t('general.warning'),
            message: response?.data.error.message,
          });
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
  },
};
</script>
<style lang="scss" scoped>
.orangehrm-button-row {
  gap: 0.4rem;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  max-width: 120px;
  margin-left: 60px;

  ::v-deep(.oxd-button--medium) {
    width: 100%;
  }

  @include oxd-respond-to('md') {
    margin-left: unset;
    max-width: unset;
    ::v-deep(.oxd-button--medium) {
      width: unset;
    }
  }
}
</style>
